<template>
  <div class="barcodes-page">
    <header class="barcodes-page__header">
      <h1>{{ $t('catalog.barcodes.title') }}</h1>
      <p>{{ $t('catalog.barcodes.subtitle') }}</p>
    </header>

    <div class="barcodes-page__layout">
      <section class="barcodes-card barcodes-settings">
        <div class="barcodes-mode">
          <button
            type="button"
            class="barcodes-mode__btn"
            :class="{ 'barcodes-mode__btn--active': mode === 'manual' }"
            @click="mode = 'manual'"
          >
            <i class="mdi mdi-keyboard-outline"></i>
            {{ $t('catalog.barcodes.modeManual') }}
          </button>
          <button
            type="button"
            class="barcodes-mode__btn"
            :class="{ 'barcodes-mode__btn--active': mode === 'random' }"
            @click="mode = 'random'"
          >
            <i class="mdi mdi-dice-multiple-outline"></i>
            {{ $t('catalog.barcodes.modeRandom') }}
          </button>
        </div>

        <v-select
          v-model="format"
          :items="formatOptions"
          item-text="label"
          item-value="value"
          outlined
          dense
          :label="$t('catalog.barcodes.format')"
          :hint="$t(`catalog.barcodes.hint_${format}`)"
          persistent-hint
          class="barcodes-settings__field"
        />

        <v-text-field
          v-if="mode === 'manual'"
          v-model.trim="manualCode"
          outlined
          dense
          clearable
          autofocus
          inputmode="numeric"
          prepend-inner-icon="mdi-barcode"
          :label="$t('catalog.barcodes.code')"
          :error-messages="manualErrorText"
          class="barcodes-settings__field"
          @keydown.enter="printLabels"
        />

        <div v-else class="barcodes-random">
          <div class="barcodes-random__code">
            <span>{{ uniqueCodes ? $t('catalog.barcodes.firstCode') : $t('catalog.barcodes.generatedCode') }}</span>
            <strong>{{ randomCodes[0] }}</strong>
          </div>
          <button type="button" class="barcodes-random__btn" @click="regenerate">
            <i class="mdi mdi-refresh"></i>
            {{ $t('catalog.barcodes.regenerate') }}
          </button>
          <v-switch
            v-model="uniqueCodes"
            inset
            dense
            hide-details
            class="barcodes-random__switch"
            :label="$t('catalog.barcodes.uniqueCodes')"
          />
        </div>

        <v-select
          v-model="labelSize"
          :items="labelSizeOptions"
          item-text="label"
          item-value="value"
          outlined
          dense
          hide-details
          :label="$t('catalog.products.labelSize')"
          class="barcodes-settings__field"
        />

        <div v-if="labelSize === 'custom'" class="barcodes-custom">
          <v-text-field
            v-model.number="customWidth"
            outlined
            dense
            hide-details
            type="number"
            min="10"
            max="200"
            :label="$t('catalog.barcodes.customWidth')"
          />
          <v-text-field
            v-model.number="customHeight"
            outlined
            dense
            hide-details
            type="number"
            min="8"
            max="200"
            :label="$t('catalog.barcodes.customHeight')"
          />
          <v-text-field
            v-model.number="customColumns"
            outlined
            dense
            hide-details
            type="number"
            min="1"
            max="6"
            :label="$t('catalog.barcodes.customColumns')"
          />
        </div>

        <v-text-field
          v-if="labelFormat.roll"
          v-model.number="labelGap"
          outlined
          dense
          type="number"
          min="0"
          max="10"
          step="0.5"
          :label="$t('catalog.barcodes.labelGap')"
          :hint="$t('catalog.barcodes.labelGapHint')"
          persistent-hint
          class="barcodes-settings__field"
        />

        <div class="barcodes-scale">
          <div class="barcodes-scale__head">
            <span>{{ $t('catalog.barcodes.barcodeScale') }}</span>
            <strong>{{ safeScale }}%</strong>
          </div>
          <v-slider
            v-model="barcodeScale"
            :min="MIN_SCALE"
            :max="100"
            :step="5"
            hide-details
            color="#0b4f8a"
            track-color="#d9e2ec"
          />
        </div>

        <div class="barcodes-qty">
          <span class="barcodes-qty__label">{{ $t('catalog.barcodes.quantity') }}</span>
          <div class="barcodes-qty__control">
            <button type="button" :disabled="safeQuantity <= 1" @click="changeQuantity(-1)">
              <i class="mdi mdi-minus"></i>
            </button>
            <input v-model.number="quantity" type="number" min="1" :max="maxQuantity" @blur="quantity = safeQuantity">
            <button type="button" :disabled="safeQuantity >= maxQuantity" @click="changeQuantity(1)">
              <i class="mdi mdi-plus"></i>
            </button>
          </div>
          <div class="barcodes-qty__presets">
            <button
              v-for="preset in quantityPresets"
              :key="preset"
              type="button"
              :class="{ 'barcodes-qty__preset--active': safeQuantity === preset }"
              @click="quantity = preset"
            >
              {{ preset }}
            </button>
          </div>
        </div>

        <v-switch
          v-model="showDigits"
          inset
          dense
          hide-details
          class="barcodes-settings__switch"
          :label="$t('catalog.barcodes.showDigits')"
        />

        <div class="barcodes-actions">
          <button
            type="button"
            class="barcodes-btn barcodes-btn--ghost barcodes-btn--icon"
            :disabled="!labels.length"
            :title="$t('catalog.barcodes.copy')"
            @click="copyCodes"
          >
            <i class="mdi mdi-content-copy"></i>
          </button>
          <button
            type="button"
            class="barcodes-btn barcodes-btn--ghost"
            :disabled="!labels.length"
            :title="$t('catalog.barcodes.browserPrintHint')"
            @click="printInBrowser"
          >
            <i class="mdi mdi-file-pdf-box"></i>
            {{ $t('catalog.barcodes.browserPrint') }}
          </button>
          <button type="button" class="barcodes-btn" :disabled="!labels.length || printing" @click="printLabels">
            <i :class="printing ? 'mdi mdi-loading mdi-spin' : 'mdi mdi-printer-outline'"></i>
            {{ $t('catalog.barcodes.print', { count: labels.length }) }}
          </button>
        </div>
      </section>

      <section class="barcodes-card barcodes-preview">
        <div class="barcodes-preview__head">
          <h2>{{ $t('catalog.barcodes.preview') }}</h2>
          <span v-if="labels.length">
            {{ $t('catalog.barcodes.summary', { labels: labels.length, pages: pages.length }) }}
          </span>
        </div>

        <div v-if="labels.length" ref="printable" class="barcodes-preview__pages">
          <section
            v-for="(page, pageIndex) in pages"
            :key="pageIndex"
            class="barcode-sheet"
            :class="labelFormat.roll ? 'barcode-sheet--roll' : 'barcode-sheet--a4'"
            :style="sheetStyle"
          >
            <article v-for="label in page" :key="label.id" class="barcode-label">
              <div class="barcode-label__bars" v-html="svgMarkup[label.code]"></div>
              <span v-if="showDigits" class="barcode-label__digits">{{ label.code }}</span>
            </article>
          </section>
        </div>

        <div v-else class="barcodes-preview__empty">
          <i class="mdi mdi-barcode-scan"></i>
          <p>{{ $t('catalog.barcodes.emptyTitle') }}</p>
          <span>{{ $t(`catalog.barcodes.hint_${format}`) }}</span>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import JsBarcode from 'jsbarcode'
import { tryPrintBarcodeLabels } from '@/api/printer'
import { LABEL_FORMATS, labelPageHeight } from '@/utils/labelFormats'
import { notify, notifyError } from '@/utils/notify'

const SETTINGS_KEY = 'barcode_generator_settings'
const MAX_QUANTITY = 500
const MIN_SCALE = 40
const DEFAULT_SCALE = 70

const CODE_FORMATS = {
  ean13: { js: 'EAN13', dataLength: 12 },
  ean8: { js: 'EAN8', dataLength: 7 },
  code128: { js: 'CODE128', maxLength: 32 },
}

// Одна наклейка на страницу — для принтеров этикеток (Xprinter, TSC и т.п.).
// width — поперёк ленты, height — вдоль ленты; linerGap — зазор между наклейками на подложке,
// scale — масштаб штрихкода по умолчанию для маленьких наклеек.
const DEFAULT_LABEL_SIZE = 'single30x20'
const DEFAULT_LINER_GAP = 2
const BARCODE_LABEL_FORMATS = {
  single30x20: { width: 30, height: 20, columns: 1, rows: 1, gap: 0, sheetWidth: 30, roll: true, linerGap: 3, scale: 80 },
  single40x30: { width: 40, height: 30, columns: 1, rows: 1, gap: 0, sheetWidth: 40, roll: true },
  single58x40: { width: 58, height: 40, columns: 1, rows: 1, gap: 0, sheetWidth: 58, roll: true },
  single58x30: { width: 58, height: 30, columns: 1, rows: 1, gap: 0, sheetWidth: 58, roll: true },
  ...LABEL_FORMATS,
}

function clamp(value, min, max, fallback) {
  const number = Number(value)
  if (!Number.isFinite(number)) return fallback
  return Math.min(max, Math.max(min, number))
}

function randomDigits(length) {
  const values = new Uint8Array(length)
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(values)
  } else {
    for (let index = 0; index < length; index += 1) values[index] = Math.floor(Math.random() * 256)
  }
  return Array.from(values, (value) => value % 10).join('')
}

function eanCheckDigit(body) {
  const sum = body
    .split('')
    .reverse()
    .reduce((acc, digit, index) => acc + Number(digit) * (index % 2 === 0 ? 3 : 1), 0)
  return String((10 - (sum % 10)) % 10)
}

// Префикс 2 — диапазон EAN для внутреннего использования магазином.
function randomCode(format) {
  if (format === 'ean13') {
    const body = `2${randomDigits(11)}`
    return body + eanCheckDigit(body)
  }
  if (format === 'ean8') {
    const body = `2${randomDigits(6)}`
    return body + eanCheckDigit(body)
  }
  return `${1 + (Number(randomDigits(1)) % 9)}${randomDigits(11)}`
}

function normalizeCode(format, raw) {
  const digits = String(raw || '').replace(/\s+/g, '')
  if (!digits) return { code: '', error: null }
  if (!/^\d+$/.test(digits)) return { code: '', error: { key: 'errorDigits' } }

  const spec = CODE_FORMATS[format]
  if (spec.dataLength) {
    const body = digits.slice(0, spec.dataLength)
    if (digits.length === spec.dataLength) {
      return { code: body + eanCheckDigit(body), error: null }
    }
    if (digits.length === spec.dataLength + 1) {
      const expected = eanCheckDigit(body)
      return digits.endsWith(expected)
        ? { code: digits, error: null }
        : { code: '', error: { key: 'errorChecksum', params: { digit: expected } } }
    }
    return { code: '', error: { key: format === 'ean8' ? 'errorEan8Length' : 'errorEan13Length' } }
  }

  if (digits.length > spec.maxLength) {
    return { code: '', error: { key: 'errorCode128Length', params: { count: spec.maxLength } } }
  }
  return { code: digits, error: null }
}

function barcodeSvg(code, jsFormat) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  try {
    JsBarcode(svg, code, {
      format: jsFormat,
      displayValue: false,
      width: 2,
      height: 80,
      margin: 0,
      marginLeft: 18,
      marginRight: 18,
      background: '#ffffff',
      lineColor: '#000000',
    })
  } catch (error) {
    return ''
  }

  // SVG растягивается на всю наклейку: ширина штрихов меняется пропорционально, сканер это читает.
  const width = parseFloat(svg.getAttribute('width')) || 0
  const height = parseFloat(svg.getAttribute('height')) || 0
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
  svg.setAttribute('preserveAspectRatio', 'none')
  svg.removeAttribute('width')
  svg.removeAttribute('height')
  svg.removeAttribute('style')
  return svg.outerHTML
}

function loadSettings() {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY)) || {}
  } catch (error) {
    return {}
  }
}

export default {
  name: 'BarcodeGenerator',
  data() {
    const saved = loadSettings()
    const savedSizeValid = !!saved.labelSize
      && (!!BARCODE_LABEL_FORMATS[saved.labelSize] || saved.labelSize === 'custom')
    const labelSize = savedSizeValid ? saved.labelSize : DEFAULT_LABEL_SIZE
    const preset = BARCODE_LABEL_FORMATS[labelSize] || {}
    const defaultGap = preset.linerGap
    return {
      mode: saved.mode === 'random' ? 'random' : 'manual',
      format: CODE_FORMATS[saved.format] ? saved.format : 'ean13',
      manualCode: '',
      randomCodes: [],
      uniqueCodes: !!saved.uniqueCodes,
      labelSize,
      customWidth: saved.customWidth || 40,
      customHeight: saved.customHeight || 30,
      customColumns: saved.customColumns || 1,
      labelGap: savedSizeValid && saved.labelGap !== undefined
        ? saved.labelGap
        : (defaultGap === undefined ? DEFAULT_LINER_GAP : defaultGap),
      scales: saved.scales && typeof saved.scales === 'object' ? saved.scales : {},
      barcodeScale: (saved.scales && saved.scales[labelSize]) || preset.scale || DEFAULT_SCALE,
      MIN_SCALE,
      printing: false,
      quantity: 1,
      showDigits: saved.showDigits !== false,
      maxQuantity: MAX_QUANTITY,
      quantityPresets: [1, 10, 20, 50, 100],
    }
  },
  computed: {
    formatOptions() {
      return [
        { value: 'ean13', label: this.$t('catalog.barcodes.formatEan13') },
        { value: 'ean8', label: this.$t('catalog.barcodes.formatEan8') },
        { value: 'code128', label: this.$t('catalog.barcodes.formatCode128') },
      ]
    },
    labelSizeOptions() {
      return [
        { value: 'single30x20', label: this.$t('catalog.barcodes.labelSizeSingle30x20') },
        { value: 'single40x30', label: this.$t('catalog.barcodes.labelSizeSingle40x30') },
        { value: 'single58x30', label: this.$t('catalog.barcodes.labelSizeSingle58x30') },
        { value: 'single58x40', label: this.$t('catalog.barcodes.labelSizeSingle58x40') },
        { value: 'roll30x20', label: this.$t('catalog.products.labelSizeRoll30x20') },
        { value: 'roll25x20', label: this.$t('catalog.products.labelSizeRoll25x20') },
        { value: 'roll25x15', label: this.$t('catalog.products.labelSizeRoll25x15') },
        { value: 'roll30x25', label: this.$t('catalog.products.labelSizeRoll30x25') },
        { value: 'mini', label: this.$t('catalog.products.labelSizeMini') },
        { value: 'compact', label: this.$t('catalog.products.labelSizeCompact') },
        { value: 'standard', label: this.$t('catalog.products.labelSizeStandard') },
        { value: 'large', label: this.$t('catalog.products.labelSizeLarge') },
        { value: 'custom', label: this.$t('catalog.barcodes.labelSizeCustom') },
      ]
    },
    safeQuantity() {
      return Math.round(clamp(this.quantity, 1, MAX_QUANTITY, 1))
    },
    safeScale() {
      return Math.round(clamp(this.barcodeScale, MIN_SCALE, 100, DEFAULT_SCALE))
    },
    manualResult() {
      return normalizeCode(this.format, this.manualCode)
    },
    manualErrorText() {
      const { error } = this.manualResult
      return error ? [this.$t(`catalog.barcodes.${error.key}`, error.params || {})] : []
    },
    codes() {
      if (this.mode === 'manual') {
        const { code } = this.manualResult
        return code ? Array(this.safeQuantity).fill(code) : []
      }
      if (this.uniqueCodes) return this.randomCodes.slice(0, this.safeQuantity)
      return this.randomCodes[0] ? Array(this.safeQuantity).fill(this.randomCodes[0]) : []
    },
    labels() {
      return this.codes.map((code, index) => ({ id: index, code }))
    },
    svgMarkup() {
      const jsFormat = CODE_FORMATS[this.format].js
      const markup = {}
      new Set(this.codes).forEach((code) => {
        markup[code] = barcodeSvg(code, jsFormat)
      })
      return markup
    },
    labelFormat() {
      if (this.labelSize === 'custom') {
        const width = clamp(this.customWidth, 10, 200, 40)
        const height = clamp(this.customHeight, 8, 200, 30)
        const columns = Math.round(clamp(this.customColumns, 1, 6, 1))
        const gap = columns > 1 ? 2 : 0
        return {
          width,
          height,
          columns,
          rows: 1,
          gap,
          sheetWidth: columns * width + (columns - 1) * gap,
          roll: true,
        }
      }
      return BARCODE_LABEL_FORMATS[this.labelSize] || BARCODE_LABEL_FORMATS.roll30x20
    },
    pages() {
      const perPage = this.labelFormat.columns * this.labelFormat.rows
      const pages = []
      for (let index = 0; index < this.labels.length; index += perPage) {
        pages.push(this.labels.slice(index, index + perPage))
      }
      return pages
    },
    sheetStyle() {
      const format = this.labelFormat
      const padding = Math.min(3, Math.max(0.8, format.height * 0.06))
      const digits = Math.min(4.2, Math.max(1.6, format.height * 0.12))
      return {
        '--label-columns': format.columns,
        '--label-width': `${format.width}mm`,
        '--label-height': `${format.height}mm`,
        '--label-gap': `${format.gap || 0}mm`,
        '--label-padding': `${padding}mm`,
        '--digits-size': `${digits}mm`,
        '--barcode-scale': this.safeScale / 100,
        '--sheet-width': format.roll ? `${format.sheetWidth}mm` : '200mm',
        '--sheet-height': format.roll ? `${labelPageHeight(format)}mm` : 'auto',
      }
    },
    settings() {
      return {
        mode: this.mode,
        format: this.format,
        uniqueCodes: this.uniqueCodes,
        labelSize: this.labelSize,
        customWidth: this.customWidth,
        customHeight: this.customHeight,
        customColumns: this.customColumns,
        labelGap: this.labelGap,
        scales: { ...this.scales },
        showDigits: this.showDigits,
      }
    },
    agentPayload() {
      const format = this.labelFormat
      return {
        format: this.format,
        codes: this.codes,
        show_digits: this.showDigits,
        scale: this.safeScale / 100,
        label: {
          width: format.width,
          height: format.height,
          columns: format.columns,
          column_gap: format.columns > 1 ? format.gap || 0 : 0,
          gap: clamp(this.labelGap, 0, 10, 2),
        },
      }
    },
  },
  watch: {
    format() {
      this.ensureRandomCodes(true)
    },
    labelSize(value) {
      const preset = BARCODE_LABEL_FORMATS[value]
      if (preset && preset.linerGap !== undefined) this.labelGap = preset.linerGap
      this.barcodeScale = this.scales[value] || (preset && preset.scale) || DEFAULT_SCALE
    },
    safeScale(value) {
      this.$set(this.scales, this.labelSize, value)
    },
    safeQuantity() {
      this.ensureRandomCodes()
    },
    uniqueCodes() {
      this.ensureRandomCodes()
    },
    settings(value) {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(value))
    },
  },
  created() {
    this.ensureRandomCodes(true)
  },
  methods: {
    ensureRandomCodes(reset = false) {
      const needed = this.uniqueCodes ? this.safeQuantity : 1
      const codes = reset ? [] : this.randomCodes.slice(0, needed)
      const seen = new Set(codes)
      while (codes.length < needed) {
        const code = randomCode(this.format)
        if (!seen.has(code)) {
          seen.add(code)
          codes.push(code)
        }
      }
      this.randomCodes = codes
    },
    regenerate() {
      this.ensureRandomCodes(true)
    },
    changeQuantity(step) {
      this.quantity = Math.round(clamp(this.safeQuantity + step, 1, MAX_QUANTITY, 1))
    },
    async copyCodes() {
      const text = Array.from(new Set(this.codes)).join('\n')
      try {
        await navigator.clipboard.writeText(text)
        notify(this.$t('catalog.barcodes.copied'))
      } catch (error) {
        notify(text, 'info')
      }
    },
    printCss() {
      const format = this.labelFormat
      const page = format.roll
        ? `@page{size:${format.sheetWidth}mm ${labelPageHeight(format)}mm;margin:0}`
        : '@page{size:A4;margin:5mm}'
      return `${page}`
        + '*{box-sizing:border-box}html,body{margin:0;padding:0;background:#fff}'
        + '.barcode-sheet{display:grid;width:var(--sheet-width);height:var(--sheet-height);grid-template-columns:repeat(var(--label-columns),var(--label-width));grid-auto-rows:var(--label-height);gap:var(--label-gap);align-content:start;overflow:hidden;page-break-after:always;break-after:page}'
        + '.barcode-sheet:last-child{page-break-after:auto;break-after:auto}'
        + '.barcode-sheet--roll{justify-content:center}.barcode-sheet--a4{justify-content:space-between}'
        + '.barcode-label{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.4mm;padding:var(--label-padding);overflow:hidden;font-family:Arial,sans-serif;color:#000}'
        + '.barcode-sheet--a4 .barcode-label{border:1px dashed #9fb0c1}'
        + '.barcode-label__bars{display:flex;flex:1 1 auto;min-height:0;width:100%;align-items:center;justify-content:center}.barcode-label__bars svg{display:block;width:calc(var(--barcode-scale) * 100%);height:calc(var(--barcode-scale) * 100%)}'
        + '.barcode-label__digits{font-size:var(--digits-size);line-height:1;letter-spacing:.06em;white-space:nowrap}'
        + '@media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}'
    },
    async printLabels() {
      if (!this.labels.length || this.printing) return
      if (!this.labelFormat.roll) {
        this.printInBrowser()
        return
      }

      this.printing = true
      try {
        const printed = await tryPrintBarcodeLabels(this.agentPayload)
        if (printed) {
          notify(this.$t('catalog.barcodes.sentToPrinter', { count: this.labels.length }))
          return
        }
        notify(this.$t('catalog.barcodes.agentOffline'), 'warning')
        this.printInBrowser()
      } catch (error) {
        notifyError(error, this.$t('catalog.barcodes.printFailed'))
      } finally {
        this.printing = false
      }
    },
    printInBrowser() {
      const source = this.$refs.printable
      if (!source || !this.labels.length) return

      const printWindow = window.open('', '_blank', 'width=960,height=720')
      if (!printWindow) {
        notify(this.$t('catalog.barcodes.popupBlocked'), 'warning')
        return
      }

      printWindow.document.write(`<!doctype html><html><head><title>Sauda barcodes</title><style>${this.printCss()}</style></head><body>${source.innerHTML}</body></html>`)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    },
  },
}
</script>

<style scoped lang="scss">
.barcodes-page {
  --ink: #152033;
  --muted: #6b7a90;
  --accent: #0b4f8a;
  --line: #d9e2ec;

  padding: 28px 32px 40px;
  color: var(--ink);
}

.barcodes-page__header {
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: var(--muted);
    font-size: 14px;
  }
}

.barcodes-page__layout {
  display: grid;
  grid-template-columns: minmax(300px, 380px) minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.barcodes-card {
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: #fff;
}

.barcodes-settings {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.barcodes-settings__field ::v-deep .v-input__slot {
  border-radius: 10px !important;
}

.barcodes-settings__switch,
.barcodes-random__switch {
  margin: 0 !important;
  padding: 0 !important;

  ::v-deep .v-label {
    color: #405168;
    font-size: 13px;
    font-weight: 650;
  }
}

.barcodes-mode {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: #eef3f7;
}

.barcodes-mode__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 8px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--muted);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  .mdi {
    font-size: 17px;
  }

  &--active {
    background: #fff;
    color: var(--accent);
    box-shadow: 0 1px 3px rgba(21, 32, 51, .12);
  }
}

.barcodes-random {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 12px;
  border-radius: 12px;
  background: #f3f8fc;
}

.barcodes-random__code {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;

  span {
    color: var(--muted);
    font-size: 11px;
    font-weight: 650;
    text-transform: uppercase;
    letter-spacing: .05em;
  }

  strong {
    overflow: hidden;
    font-family: 'Courier New', monospace;
    font-size: 18px;
    letter-spacing: .06em;
    text-overflow: ellipsis;
  }
}

.barcodes-random__btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 11px;
  border: 0;
  border-radius: 9px;
  background: #dfeef9;
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.barcodes-random__switch {
  grid-column: 1 / -1;
}

.barcodes-custom {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.barcodes-qty {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.barcodes-qty__label {
  color: #3d4f63;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .07em;
  text-transform: uppercase;
}

.barcodes-qty__control {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 44px;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 10px;

  button {
    border: 0;
    background: #f3f6f9;
    color: var(--ink);
    font-size: 18px;
    cursor: pointer;

    &:disabled {
      color: #b4c0cc;
      cursor: default;
    }
  }

  input {
    width: 100%;
    padding: 9px 6px;
    border: 0;
    outline: none;
    font-size: 16px;
    font-weight: 700;
    text-align: center;
  }
}

.barcodes-qty__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  button {
    padding: 5px 11px;
    border: 1px solid var(--line);
    border-radius: 999px;
    background: #fff;
    color: var(--ink);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }

  .barcodes-qty__preset--active {
    border-color: var(--accent);
    background: var(--accent);
    color: #fff;
  }
}

.barcodes-actions {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  gap: 8px;
  margin-top: 4px;
}

.barcodes-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 12px 14px;
  border: 0;
  border-radius: 11px;
  background: var(--accent);
  color: #fff;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: .5;
    cursor: default;
  }

  &--ghost {
    background: #e4ebf2;
    color: #3d4f63;
  }

  &--icon {
    padding: 12px;
  }
}

.barcodes-preview {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 12px;
  background: #f2f5f8;
}

.barcodes-preview__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;

  h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: .06em;
  }

  span {
    color: var(--muted);
    font-size: 13px;
    font-weight: 650;
  }
}

.barcodes-preview__pages {
  display: flex;
  max-height: 70vh;
  overflow: auto;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 4px;
}

.barcode-sheet {
  display: grid;
  flex: 0 0 auto;
  width: var(--sheet-width);
  height: var(--sheet-height);
  grid-template-columns: repeat(var(--label-columns), var(--label-width));
  grid-auto-rows: var(--label-height);
  gap: var(--label-gap);
  align-content: start;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(21, 32, 51, .14);

  &--roll {
    justify-content: center;
  }

  &--a4 {
    justify-content: space-between;
    padding: 2mm;
  }
}

.barcode-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .4mm;
  padding: var(--label-padding);
  overflow: hidden;
  font-family: Arial, sans-serif;
  color: #000;
  outline: 1px dashed #c4d1dd;
  outline-offset: -1px;

  .barcode-sheet--a4 & {
    outline: 0;
    border: 1px dashed #9fb0c1;
  }
}

.barcode-label__bars {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  align-items: center;
  justify-content: center;

  ::v-deep svg {
    display: block;
    width: calc(var(--barcode-scale) * 100%);
    height: calc(var(--barcode-scale) * 100%);
  }
}

.barcodes-scale__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  span {
    color: #3d4f63;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: .07em;
    text-transform: uppercase;
  }

  strong {
    color: var(--accent);
    font-size: 14px;
  }
}

.barcode-label__digits {
  font-size: var(--digits-size);
  line-height: 1;
  letter-spacing: .06em;
  white-space: nowrap;
}

.barcodes-preview__empty {
  display: flex;
  min-height: 260px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px dashed var(--line);
  border-radius: 14px;
  background: #fff;
  color: var(--muted);
  text-align: center;

  .mdi {
    font-size: 42px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: var(--ink);
    font-size: 16px;
    font-weight: 700;
  }

  span {
    max-width: 320px;
    font-size: 13px;
  }
}

@media (max-width: 900px) {
  .barcodes-page {
    padding: 20px 16px 32px;
  }

  .barcodes-page__layout {
    grid-template-columns: 1fr;
  }
}
</style>
